import type { ComputedRef } from 'vue';
import type { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import { isDefined } from '~/utils/guards/isDefined';
import type { StoreConfig } from '~/lib/Shop/generated/schema';

export type RelevantStoreConfig = Pick<
  StoreConfig,
  | 'store_code'
  | 'grid_per_page'
  | 'checkout_coupon_code_active'
  | 'sales_minimum_order_active'
  | 'sales_minimum_order_amount'
  | 'sales_minimum_order_description'
  | 'sales_minimum_order_tax_including'
  | 'sales_minimum_order_include_discount_amount'
  | 'maxqty_enabled'
  | 'maxqty_attribute_sets'
  | 'maxqty_qty'
  | 'tax_exemption_enabled'
  | 'tax_exemption_selected_region'
  | 'tax_exemption_file_size'
  | 'tax_exemption_file_extensions'
  | 'tax_exemption_field_title'
  | 'tax_exemption_field_info'
  | 'adyen_store_locale'
  | 'adyen_client_key_live'
  | 'adyen_client_key_test'
  | 'adyen_demo_mode'
  | 'adyen_has_holder_name'
  | 'adyen_holder_name_required'
  | 'region_required_countries'
  | 'vat_validation_enabled'
  | 'vat_validation_frontend_enabled'
  | 'vat_validation_config'
  | 'quote_form_specificcountry'
  | 'quote_form_language'
  | 'quote_form_agreement'
>;

export default defineNuxtPlugin(async () => {
  const { $locale } = useNuxtApp();

  function createProductDetailLink(product: DecoratedProduct) {
    const locale = useLocale();

    return buildPdpUrl(locale.value, product.urlKey ?? '');
  }

  const configuration = usePdpConfiguration();

  const shopStore: ComputedRef<string> = computed<string>(
    () =>
      configuration.value?.localeStoreMap?.[$locale.value] ??
      configuration.value?.localeStoreMap?.default ??
      'emea_en'
  );

  const usesDemoShop = useState<boolean>('usesDemoShop');
  const storeConfig = useState<RelevantStoreConfig>('storeConfig');

  if (!isDefined(usesDemoShop.value)) {
    const config = useRuntimeConfig().magento;

    const magentoUrl = (() => {
      if ($locale.value === 'zh-cn') {
        return config.configurationByLocale?.['zh-cn']?.graphql.url;
      }
      return config.defaultConfiguration.graphql.url;
    })();

    usesDemoShop.value = !magentoUrl?.startsWith('https://shop.baslerweb');
  }

  if (!isDefined(storeConfig.value) && $locale.value !== 'zh-cn') {
    storeConfig.value =
      (await useStoreConfig($locale.value, shopStore.value)).value ?? {};
  }

  return {
    provide: {
      createProductDetailLink,
      shopStore,
      usesDemoShop,
      storeConfig
    }
  };
});
